/**
 * Pestaña Publicaciones del repositorio de Redes Sociales: posts del periodo con miniatura,
 * búsqueda en el texto (la hace el motor), filtro por tipo, orden por cualquier métrica,
 * paginación, exportación y un panel con el detalle del post. Solo se muestran las métricas
 * que reportan las redes de los posts visibles.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ink, studio, SearchField, FilterPill } from '../admin/studioUi';
import { downloadCsv } from './repoApi';
import usePeriodPosts from './usePeriodPosts';
import NetworkDot from './NetworkDot';
import InfoTip from './InfoTip';
import { rt, useFitColumns, HiddenColumnsNote } from './repoTable';
import LoadingBlock from './LoadingBlock';
import { evaluateKpi, formatKpi } from './customKpis';
import {
    driveThumbnail, formatCompact, formatMetric, isBlank, isDurationMetric, matchesSelection, metricsWithData, num,
} from './socialRepoUtils';

const PAGE_SIZE = 50;
/* Métricas que se ven en la tabla, en este orden, si la red las reporta. */
const TABLE_METRICS = ['Alcance', 'Impresiones', 'Vistas', 'Interacciones', 'Likes', 'Comentarios', 'Compartidos', 'Guardados', 'Clics'];

const formatDate = (value) => {
    const text = String(value || '');
    const date = new Date(text.length <= 10 ? `${text}T00:00:00` : text);
    if (Number.isNaN(date.getTime())) return text.slice(0, 16);
    return date.toLocaleString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export function Thumb({ url, size = 56 }) {
    const [failed, setFailed] = useState(false);
    const src = driveThumbnail(url, size * 4);
    const box = { width: size, height: size, borderRadius: 8, flexShrink: 0, backgroundColor: ink.wash, objectFit: 'cover', display: 'block' };
    if (!src || failed) return <div style={{ ...box, display: 'flex', alignItems: 'center', justifyContent: 'center', color: ink.faint, fontSize: '0.7rem' }}>sin imagen</div>;
    return <img src={src} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setFailed(true)} style={box} />;
}

export function PostDetail({ post, metrics, kpis, onClose }) {
    useEffect(() => {
        const onKey = (event) => { if (event.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);
    return createPortal(
        <div style={styles.backdrop} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <aside style={styles.drawer} role="dialog" aria-modal="true" aria-label="Detalle de la publicación">
                <div style={styles.drawerHead}>
                    <span style={styles.redCell}><NetworkDot red={post.Red} size={10} /><strong>{post.Perfil}</strong> · {post.Red}</span>
                    <button type="button" onClick={onClose} style={studio.iconBtn} aria-label="Cerrar">×</button>
                </div>
                <p style={studio.hint}>{formatDate(post['Fecha Publicación'])} · {post.Tipo}{!isBlank(post.Formato) ? ` · ${post.Formato}` : ''}</p>
                <Thumb url={post['Imagen Drive']} size={320} />
                {!isBlank(post.Texto) && <p style={styles.fullText}>{post.Texto}</p>}
                <div style={styles.metricGrid}>
                    {metrics.map(metric => (
                        <div key={metric} style={styles.metricCell}>
                            <span style={{ ...studio.statLabel, display: 'inline-flex', alignItems: 'center' }}>{metric.replace(' (s)', '')}<InfoTip term={metric} /></span>
                            <span style={styles.metricValue}>{formatMetric(metric, post[metric])}</span>
                        </div>
                    ))}
                    {kpis.map(kpi => (
                        <div key={kpi.id} style={{ ...styles.metricCell, backgroundColor: '#fff8e1' }} title={kpi.formula}>
                            <span style={studio.statLabel}>{kpi.name}</span>
                            <span style={styles.metricValue}>{formatKpi(kpi, evaluateKpi(kpi, post))}</span>
                        </div>
                    ))}
                </div>
                {!isBlank(post.Enlace) && (
                    <a href={post.Enlace} target="_blank" rel="noreferrer" style={{ ...studio.primaryBtn, textDecoration: 'none', textAlign: 'center' }}>
                        Ver publicación en {post.Red}
                    </a>
                )}
            </aside>
        </div>,
        document.body
    );
}

const SocialPosts = ({ repo, kpis = [], selection, period, dataVersion, narrow }) => {
    /* En un post solo aplican los KPIs hechos con métricas de publicaciones. */
    const postKpis = useMemo(() => kpis.filter(kpi => kpi.groups.length === 1 && kpi.groups[0] === 'posts'), [kpis]);
    const [search, setSearch] = useState('');
    const [term, setTerm] = useState('');
    const [type, setType] = useState('');
    const [sortKey, setSortKey] = useState('Fecha Publicación');
    const [page, setPage] = useState(0);
    const [detail, setDetail] = useState(null);

    /* La búsqueda va al motor con una pausa corta para no consultar en cada tecla. */
    useEffect(() => {
        const timer = setTimeout(() => setTerm(search.trim()), 400);
        return () => clearTimeout(timer);
    }, [search]);

    const state = usePeriodPosts(repo, selection, period, { term, dataVersion });

    useEffect(() => { setPage(0); }, [period, selection, term, type, sortKey]);

    const visible = useMemo(
        () => state.rows.filter(row => matchesSelection(row, selection) && (!type || row.Tipo === type)),
        [state.rows, selection, type]
    );
    const types = useMemo(
        () => Array.from(new Set(state.rows.map(row => row.Tipo).filter(t => !isBlank(t)))).sort(),
        [state.rows]
    );
    const metrics = useMemo(() => metricsWithData(visible), [visible]);
    const tableMetrics = TABLE_METRICS.filter(m => metrics.includes(m)).slice(0, narrow ? 3 : TABLE_METRICS.length);
    /* Columnas de la tabla; las que no caben en el ancho se ocultan por prioridad. */
    const columns = useMemo(() => [
        { key: 'post', label: 'Publicación', min: 240 },
        { key: 'red', label: 'Red', min: 84 },
        { key: 'perfil', label: 'Perfil', min: 120, priority: 1 },
        { key: 'fecha', label: 'Fecha', min: 86, priority: 2 },
        ...tableMetrics.map((m, i) => ({ key: m, label: m, metric: m, num: true, min: 70, priority: 3 + i })),
        ...postKpis.map(kpi => ({ key: `kpi:${kpi.id}`, label: kpi.name, kpi, num: true, min: 78, priority: 4 })),
    ], [tableMetrics.join('|'), postKpis]); // eslint-disable-line react-hooks/exhaustive-deps -- tableMetrics se compara por contenido
    const [tableRef, visibleColumns, hiddenColumns] = useFitColumns(columns);
    const withKpis = useMemo(() => (postKpis.length
        ? visible.map(row => ({ ...row, ...Object.fromEntries(postKpis.map(kpi => [`kpi:${kpi.id}`, evaluateKpi(kpi, row)])) }))
        : visible), [visible, postKpis]);
    const sorted = useMemo(() => {
        if (sortKey === 'Fecha Publicación') return withKpis;
        const value = (row) => {
            const v = row[sortKey];
            return v == null || v === '' ? -Infinity : num(v);
        };
        return [...withKpis].sort((a, b) => value(b) - value(a));
    }, [withKpis, sortKey]);
    const pages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
    const pageRows = sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

    const exportPosts = () => downloadCsv(`redes-sociales-publicaciones-${period.from}_${period.to}.csv`, [
        { label: 'Fecha', value: r => r['Fecha Publicación'] },
        { label: 'Red', value: r => r.Red },
        { label: 'Perfil', value: r => r.Perfil },
        { label: 'Tipo', value: r => r.Tipo },
        { label: 'Texto', value: r => (isBlank(r.Texto) ? '' : r.Texto) },
        { label: 'Enlace', value: r => (isBlank(r.Enlace) ? '' : r.Enlace) },
        ...metrics.map(m => ({ label: m, value: r => num(r[m]) })),
        ...postKpis.map(kpi => ({ label: kpi.name, value: r => (r[`kpi:${kpi.id}`] == null ? '' : r[`kpi:${kpi.id}`]) })),
    ], sorted);

    return (
        <section style={studio.card}>
            <div style={styles.toolbar}>
                <SearchField value={search} onChange={setSearch} placeholder="Buscar en el texto de las publicaciones…" style={{ marginBottom: 0, flex: '1 1 260px' }} />
                <FilterPill value={type} onChange={setType} style={{ minWidth: 150, ...(narrow ? { flex: 1 } : {}) }}>
                    <option value="">Todos los tipos</option>
                    {types.map(t => <option key={t} value={t}>{t}</option>)}
                </FilterPill>
                <FilterPill value={sortKey} onChange={setSortKey} style={{ minWidth: 190, ...(narrow ? { flex: 1 } : {}) }}>
                    <option value="Fecha Publicación">Más recientes</option>
                    {metrics.map(m => <option key={m} value={m}>Mayor {m.replace(' (s)', '').toLowerCase()}</option>)}
                    {postKpis.map(kpi => <option key={kpi.id} value={`kpi:${kpi.id}`}>Mayor {kpi.name}</option>)}
                </FilterPill>
                <button type="button" onClick={exportPosts} style={studio.ghostBtn} disabled={!sorted.length}>Exportar CSV</button>
            </div>
            <p style={{ ...studio.hint, margin: '0 0 12px' }}>
                {state.loading ? 'Consultando…' : `${sorted.length.toLocaleString('es-MX')} publicaciones`}
                {state.truncated && ' · se muestran las 5,000 más recientes; acota el periodo o los perfiles'}
            </p>
            {state.error && <div style={styles.error}>{state.error}</div>}
            {state.loading && !state.rows.length && <LoadingBlock rows={2} label="Consultando publicaciones…" />}

            {narrow ? (
                <div style={styles.cardList}>
                    {pageRows.map(row => (
                        <button key={row['Post ID']} type="button" onClick={() => setDetail(row)} style={styles.postCard}>
                            <Thumb url={row['Imagen Drive']} size={64} />
                            <div style={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
                                <div style={styles.redCell}><NetworkDot red={row.Red} />{row.Perfil}</div>
                                <div style={styles.dateText}>{formatDate(row['Fecha Publicación'])}</div>
                                <div style={styles.textClamp}>{isBlank(row.Texto) ? '' : row.Texto}</div>
                                <div style={styles.cardMetrics}>
                                    {tableMetrics.map(m => <span key={m}>{m}: <strong>{formatCompact(num(row[m]))}</strong></span>)}
                                    {postKpis.map(kpi => <span key={kpi.id} style={{ color: ink.accentText }}>{kpi.name}: <strong>{formatKpi(kpi, row[`kpi:${kpi.id}`])}</strong></span>)}
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            ) : (
                <div ref={tableRef}>
                    <table style={rt.table}>
                        <thead>
                            <tr>
                                {visibleColumns.map((col) => {
                                    const sortId = col.metric || (col.kpi ? `kpi:${col.kpi.id}` : null);
                                    return (
                                        <th
                                            key={col.key}
                                            title={col.kpi ? col.kpi.formula : undefined}
                                            onClick={sortId ? () => setSortKey(sortKey === sortId ? 'Fecha Publicación' : sortId) : undefined}
                                            style={{ ...rt.th, textAlign: col.num ? 'right' : 'left', cursor: sortId ? 'pointer' : 'default', ...(col.kpi ? { color: ink.accentText } : {}) }}
                                        >
                                            {col.label}{sortId && sortKey === sortId ? ' ↓' : ''}
                                            {col.metric && <InfoTip term={col.metric} />}
                                            {col.kpi && <InfoTip term="kpiPropio" how={`Fórmula: ${col.kpi.formula}, con los valores de cada post.`} />}
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {pageRows.map(row => (
                                <tr key={row['Post ID']} onClick={() => setDetail(row)} style={{ cursor: 'pointer' }}>
                                    {visibleColumns.map((col) => {
                                        if (col.key === 'post') {
                                            return (
                                                <td key={col.key} style={rt.td}>
                                                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                                        <Thumb url={row['Imagen Drive']} size={48} />
                                                        <div style={{ minWidth: 0 }}>
                                                            <div style={styles.textClamp}>{isBlank(row.Texto) ? <span style={{ color: ink.faint }}>Sin texto</span> : row.Texto}</div>
                                                            <div style={styles.dateText}>
                                                                {row.Tipo}
                                                                {!visibleColumns.some(c => c.key === 'fecha') && ` · ${formatDate(row['Fecha Publicación'])}`}
                                                                {!visibleColumns.some(c => c.key === 'perfil') && ` · ${row.Perfil}`}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            );
                                        }
                                        if (col.key === 'red') return <td key={col.key} style={rt.td}><span style={styles.redCell}><NetworkDot red={row.Red} />{row.Red}</span></td>;
                                        if (col.key === 'perfil') return <td key={col.key} style={{ ...rt.td, fontWeight: 600 }}>{row.Perfil}</td>;
                                        if (col.key === 'fecha') return <td key={col.key} style={{ ...rt.td, color: ink.muted, fontSize: '0.78rem' }}>{formatDate(row['Fecha Publicación'])}</td>;
                                        if (col.kpi) return <td key={col.key} style={{ ...rt.num, color: ink.accentText }}>{formatKpi(col.kpi, row[`kpi:${col.kpi.id}`])}</td>;
                                        return <td key={col.key} style={rt.num} title={formatMetric(col.metric, row[col.metric])}>{isDurationMetric(col.metric) ? formatMetric(col.metric, row[col.metric]) : formatCompact(num(row[col.metric]))}</td>;
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <HiddenColumnsNote hidden={hiddenColumns} />
                </div>
            )}
            {!state.loading && !sorted.length && !state.error && (
                <p style={{ ...studio.hint, textAlign: 'center', padding: 24 }}>No hay publicaciones con estos filtros.</p>
            )}

            {pages > 1 && (
                <div style={styles.pager}>
                    <button type="button" style={studio.ghostBtn} disabled={page === 0} onClick={() => setPage(p => p - 1)}>Anterior</button>
                    <span style={studio.hint}>Página {page + 1} de {pages}</span>
                    <button type="button" style={studio.ghostBtn} disabled={page >= pages - 1} onClick={() => setPage(p => p + 1)}>Siguiente</button>
                </div>
            )}

            {detail && <PostDetail post={detail} metrics={metricsWithData([detail])} kpis={postKpis} onClose={() => setDetail(null)} />}
        </section>
    );
};

const styles = {
    toolbar: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 },
    tableScroll: { overflowX: 'auto' },
    numTd: { ...studio.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
    redCell: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.86rem' },
    textClamp: {
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        fontSize: '0.86rem',
        color: ink.text,
        lineHeight: 1.4,
        wordBreak: 'break-word',
    },
    dateText: { fontSize: '0.76rem', color: ink.faint, marginTop: 2 },
    pager: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 14, marginTop: 16 },
    error: { padding: '10px 12px', borderRadius: 10, backgroundColor: ink.dangerSoft, color: ink.danger, fontSize: '0.86rem', marginBottom: 12 },
    cardList: { display: 'flex', flexDirection: 'column', gap: 10 },
    postCard: {
        display: 'flex',
        gap: 12,
        padding: 10,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        borderRadius: 12,
        backgroundColor: ink.surface,
        fontFamily: 'inherit',
        cursor: 'pointer',
    },
    cardMetrics: { display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: '0.74rem', color: ink.muted, marginTop: 6 },
    backdrop: { position: 'fixed', inset: 0, backgroundColor: 'rgba(28, 29, 33, 0.32)', zIndex: 2000, display: 'flex', justifyContent: 'flex-end' },
    drawer: {
        width: 'min(440px, 100%)',
        height: '100%',
        overflowY: 'auto',
        backgroundColor: ink.surface,
        padding: '20px 22px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        boxShadow: '-12px 0 40px rgba(28, 29, 33, 0.18)',
    },
    drawerHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 },
    fullText: { margin: 0, whiteSpace: 'pre-wrap', fontSize: '0.9rem', lineHeight: 1.5, color: ink.text },
    metricGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 },
    metricCell: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        padding: '10px 12px',
        borderRadius: 10,
        backgroundColor: ink.wash,
    },
    metricValue: { fontSize: '1.1rem', fontWeight: 600, color: ink.text },
};

export default SocialPosts;
